import { useMemo, useState } from 'react';
import Plot from 'react-plotly.js';
import { chartColors } from '../../helpers/chartColors';
import { rtfoConversionFactors } from './rtfoConversionFactors';

export const RTFOPressureAnalysisGrowthChart = ({ currentRTFO, monitorUWI, monitorWellEvents, fiberData, gridView, theme, darkMode, showSpikeLines, showLegend }) => {

  const [avgVFR, setAvgVFR] = useState(0);
  const [avgDiffusivityLow, setAvgDiffusivityLow] = useState(0);
  const [avgDiffusivityHigh, setAvgDiffusivityHigh] = useState(0);
  const units = currentRTFO?.settings?.units || 'metric';

  const data = useMemo(() => {
    if (((!monitorWellEvents || monitorWellEvents?.length === 0) && (!fiberData || fiberData?.length === 0))) {
      return [];
    };

    return getChartData();

    function getChartData() {
      try {
  
        const traces = [];
        
        if (monitorWellEvents?.length > 0 || fiberData?.length > 0) {
          let filteredEvents = [];
          filteredEvents = filteredEvents.concat(monitorWellEvents?.filter(e => e && e.name && e.stageName && e?.name !== 'Stage Start')); // add pressure events
          filteredEvents = filteredEvents.concat(fiberData?.filter(e => e && e.name && e.stageName && e?.name !== 'Stage Start')); // add fiber events

          const groups = [...new Set(filteredEvents?.map(e => (`${e?.stageName?.split('_')?.[1]}`)))];
  
          for (let i = 0; i < groups.length; i++) {
            const group = groups[i];
            const groupEvents = filteredEvents.filter(e => e?.stageName?.split('_')?.[1] === group);
            const wellName = group?.split('_')?.[1];

            const X = [];
            const Y = [];
            groupEvents.forEach(g => {
              X.push((g.slurryVolume || g.vfr) * (units === 'field' ? rtfoConversionFactors.slurryVolume : 1));
              Y.push(g.distance * (units === 'field' ? rtfoConversionFactors.depth : 1));
            });
  
            traces.push({
              x: X,
              y: Y,
              mode: 'markers',
              type: 'scattergl',
              xaxis: 'x1',
              yaxis: 'y1',
              name: group,
              marker: {
                size: 10,
                color: chartColors[i % chartColors.length]
              },
              legendgroup: wellName,
              connectgaps: true,
              hoverinfo: !showSpikeLines ? 'none' : 'text',
              hovertext: Y.map(y => `${group} Xf: ${y?.toFixed(2)} ${units === 'field' ? 'ft' : 'm'}`),
            });
          };
        };


        // Add fit line
        const X = 
          units === 'field' ?
          [1, 600, 1200, 1800, 2400, 3000, 3600, 4200, 4800, 5400, 6000, 6600, 7200, 7800, 8400, 9000, 9600, 10200, 10800, 11400, 12000] :
          [1, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000, 1100, 1200, 1300, 1400, 1500, 1600, 1700, 1800, 1900, 2000];

        const diffusivities = [];
        monitorWellEvents?.forEach(e => e.name !== 'Stage Start' && (e.diffusivity > 0 && isFinite(e.diffusivity) && !isNaN(e.diffusivity) && diffusivities.push(e.diffusivity * (units === 'field' ? rtfoConversionFactors.diffusivity : 1))));
        fiberData?.forEach(e => (e.diffusivity > 0 && isFinite(e.diffusivity) && !isNaN(e.diffusivity) && diffusivities.push(e.diffusivity * (units === 'field' ? rtfoConversionFactors.diffusivity : 1))));
        diffusivities?.sort((a, b) => a - b);

        const avgLow = Math.min(...diffusivities);
        const avgHigh = Math.max(...diffusivities);
        const avgAvg = diffusivities?.reduce((tot, curr) => tot + curr, 0) / diffusivities?.length;

        let vfrArray = [];
        monitorWellEvents?.length > 0 && monitorWellEvents?.forEach(e => e.name !== 'Stage Start' && (vfrArray.push(currentRTFO?.stageData?.[e.stageName]?.metrics?.totalSlurryVolume)));
        fiberData?.length > 0 && fiberData?.forEach(e => e.name !== 'Stage Start' && (vfrArray.push(currentRTFO?.stageData?.[e.stageName]?.metrics?.totalSlurryVolume)));

        const avgVFR = ((vfrArray?.reduce((tot, curr) => tot + curr, 0) / vfrArray?.length) * (units === 'field' ? rtfoConversionFactors.vfr : 1));
        setAvgVFR(avgVFR);

        setAvgDiffusivityLow(avgLow);
        setAvgDiffusivityHigh(avgHigh);

        const YAvgLow = X.map(x => (Math.sqrt(4 * Math.PI * x * avgLow)));
        const YAvgHigh = X.map(x => (Math.sqrt(4 * Math.PI * x * avgHigh)));
        const YAvgAvg = X.map(x => (Math.sqrt(4 * Math.PI * x * avgAvg)));

        traces.push({
          x: X,
          y: YAvgLow,
          mode: 'line',
          type: 'scatter',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Normal Growth Rate Low',
          line: {
            weight: 3,
            color: 'green',
            shape: 'spline',
            smoothing: 1.3
          },
          marker: {
            size: 0,
            color: 'transparent'
          },
          connectgaps: true,
          hoverinfo: !showSpikeLines ? 'none' : 'text',
          hovertext: YAvgLow.map(y => `Normal Growth Rate Low: ${y?.toFixed(2)}`),
        })

        traces.push({
          x: X,
          y: YAvgAvg,
          mode: 'line',
          type: 'scatter',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Normal Growth Rate Avg',
          line: {
            weight: 3,
            color: 'black',
            shape: 'spline',
            smoothing: 1.3,
            dash: 'dash'
          },
          marker: {
            size: 0,
            color: 'transparent'
          },
          connectgaps: true,
          hoverinfo: !showSpikeLines ? 'none' : 'text',
          hovertext: YAvgAvg.map(y => `Normal Growth Rate Avg: ${y?.toFixed(2)}`),
        })

        traces.push({
          x: X,
          y: YAvgHigh,
          mode: 'line',
          type: 'scatter',
          xaxis: 'x1',
          yaxis: 'y1',
          name: 'Normal Growth Rate High',
          line: {
            weight: 3,
            color: 'red',
            shape: 'spline',
            smoothing: 1.3
          },
          marker: {
            size: 0,
            color: 'transparent'
          },
          connectgaps: true,
          hoverinfo: !showSpikeLines ? 'none' : 'text',
          hovertext: YAvgHigh.map(y => `Normal Growth Rate High: ${y?.toFixed(2)}`),
        })

        return traces;
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [currentRTFO?.stageData, fiberData, monitorWellEvents, showSpikeLines, units]);

  const layout = {
    // grid: {rows: 3, columns: 2, pattern: 'independent'},
    yaxis: { // Distance y axis
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      title: {
        text: `Distance (${units === 'field' ? 'ft' : 'm'})`,
        font: {
          size: 12
        },
      },
      position: 0,
      anchor: 'x',
      side: 'left',
      range: units === 'field' ? [0, 4500] : [0, 1500]
    },
    xaxis: { // Volume x axis
      title: {
        text: `VFR (${units === 'field' ? 'bbl' : 'm3'})`,
        font: {
          size: 12
        }
      },
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
      range: units === 'field' ? [0, 12000] : [0, 2000]
    },
    hovermode: "x",
    hoverdistance: 10, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    // showlegend: showLegend ? true : false,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      y: (gridView ? -0.2 : -0.15),
    },
    annotations: [
      {
        text: "Fracture Growth Rate",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.175 : 1.075), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Pad: ${currentRTFO?.name}`,
        // text: `Pad: ${currentRTFO?.name} | Observation Well: ${monitorUWI || 'n/a'}`,
          font: {
          size: 10,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.1 : 1.035), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `<b>Xf Low: ${(Math.sqrt(4 * Math.PI * avgVFR * avgDiffusivityLow))?.toFixed(1)} ${units === 'field' ? 'ft' : 'm'}</b>`,
          font: {
            size: 14,
            color: darkMode ? '#eee' : 'green'
          },
        align: 'center',
        x: 0, //position in x domain
        y: (Math.sqrt(4 * Math.PI * avgVFR * avgDiffusivityLow)),
        xref: 'paper',
        yref: 'y',
        showarrow: true,
        arrowcolor: darkMode ? '#eee' : 'green',
        arrowhead: 3,
        ax: 75,
        ay: -50
      },
      {
        text: `<b>Xf High: ${(Math.sqrt(4 * Math.PI * avgVFR * avgDiffusivityHigh))?.toFixed(1)} ${units === 'field' ? 'ft' : 'm'}</b>`,
          font: {
            size: 14,
            color: darkMode ? '#eee' : 'red'
          },
        align: 'center',
        x: 0, //position in x domain
        y: (Math.sqrt(4 * Math.PI * avgVFR * avgDiffusivityHigh)),
        xref: 'paper',
        yref: 'y',
        showarrow: true,
        arrowcolor: darkMode ? '#eee' : 'red',
        arrowhead: 3,
        ax: 75,
        ay: -50
      }
    ],
    shapes: [
      {
        type: 'line',
        xref: 'x',
        yref: 'y',
        x0: avgVFR,
        y0: 0,
        x1: avgVFR,
        y1: (Math.sqrt(4 * Math.PI * avgVFR * avgDiffusivityLow)),
        line: {
          color: 'green',
          width: 2,
          dash: 'dot'
        }
      },
      {
        type: 'line',
        xref: 'x',
        yref: 'y',
        x0: 0,
        y0: (Math.sqrt(4 * Math.PI * avgVFR * avgDiffusivityLow)),
        x1: avgVFR,
        y1: (Math.sqrt(4 * Math.PI * avgVFR * avgDiffusivityLow)),
        line: {
          color: 'green',
          width: 2,
          dash: 'dot'
        }
      },
      {
        type: 'line',
        xref: 'x',
        yref: 'y',
        x0: avgVFR,
        y0: 0,
        x1: avgVFR,
        y1: (Math.sqrt(4 * Math.PI * avgVFR * avgDiffusivityHigh)),
        line: {
          color: 'red',
          width: 2,
          dash: 'dot'
        }
      },
      {
        type: 'line',
        xref: 'x',
        yref: 'y',
        x0: 0,
        y0: (Math.sqrt(4 * Math.PI * avgVFR * avgDiffusivityHigh)),
        x1: avgVFR,
        y1: (Math.sqrt(4 * Math.PI * avgVFR * avgDiffusivityHigh)),
        line: {
          color: 'red',
          width: 2,
          dash: 'dot'
        }
      },
    ],
    uirevision: true,
    margin: {"t": 60, "l": 60, "r": 60, "b": 60},
    images: [
      {
        x: (gridView ? 0.07 : 0.035),
        y: (gridView ? 1.05 : 1.025),
        sizex: (gridView ? 0.1 : 0.05),
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "y1 domain",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };
  
  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: `${currentRTFO?.name}-Pressure-Analysis ${(new Date().toISOString().split('T')[0])}`,
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    }
  };


  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%'}}
    />
  );
};
